import React, { useRef, useState, useCallback, useEffect } from 'react';
import {
  IonCard,
  IonHeader,
  IonIcon,
  IonContent,
  IonPage,
  IonToolbar,
  IonButtons,
  IonTitle,
  IonButton,
  IonText,
  IonCol,
  IonBackButton,
  IonGrid,
  IonRow,
  IonItem,
  IonLabel,
  IonProgressBar,
  IonCardHeader,
  IonFab,
  IonFabButton,
} from '@ionic/react';
import {
  caretBack,
  caretForward,
  listOutline as agendaIcon,
  add,
  chevronBack
} from 'ionicons/icons';
import { motion, useMotionValue, animate, useTransform } from 'motion/react';
import { TbCalendar, TbCalendarMonth } from 'react-icons/tb';
import styles from './TeamCalendar.module.scss';
import TeamAgenda, { type TeamEvent } from './TeamAgenda';
import { addDays, isSameDay, startOfDay, startOfWeek, addWeeks } from 'date-fns';
import PortalDropdown from '../../components/PortalDropdown';
import TodayCalendarIcon from '../../components/TodayCalendarIcon';
import { useTranslation } from 'react-i18next';
import { intlLocale, getWeekdayNames } from '../../i18n/locale';

const MONTH_WEEK_HEIGHT = 28;
const WEEK_VIEW_HEIGHT = 0;
const HEADER_HEIGHT = 40;
const WEEKDAY_HEADER_HEIGHT = 24;
const BASE_HEIGHT = HEADER_HEIGHT + WEEKDAY_HEADER_HEIGHT;
const MIN_HEIGHT = BASE_HEIGHT + WEEK_VIEW_HEIGHT;
const MONTH_GAP = 10;

const TeamCalendar: React.FC = () => {
  const { t } = useTranslation();
  const height = useMotionValue(MIN_HEIGHT);
  const x = useMotionValue(0);
  const xTransform = useTransform(x, (value) => `translateX(calc(-33.333% + ${value}px))`);
  const containerRef = useRef<HTMLIonCardElement>(null);
  const lastExpandedChangeRef = useRef(0);

  const touchState = useRef({
    isDragging: false,
    isNavigating: false,
    isAgendaScrolling: false,
    isNavigationUpdate: false,
    swipe: {
      isActive: false,
      isVertical: false,
      startX: 0,
      startY: 0,
      initialHeight: 0
    }
  });

  const isAutoScrollingRef = useRef(false);

  // Truncated to the 1st of the month, no time-of-day — computed directly in
  // the lazy initializer (not a mount effect) since it only ever needs to
  // happen once; re-setting selectedDate to a fresh Date object (same
  // moment, different reference) in an effect would force a second render,
  // since Dates are never reference-equal.
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [isExpandedState, setIsExpandedOriginal] = useState(false);
  const [teamEvents] = useState<TeamEvent[]>([]); // Placeholder for team events
  const [isLoadingEvents] = useState(false);

  const setIsExpanded = useCallback((value: boolean) => {
    const now = Date.now();
    if (now - lastExpandedChangeRef.current < 100) {
      return;
    }

    lastExpandedChangeRef.current = now;
    setIsExpandedOriginal(value);
  }, []);

  const isExpanded = isExpandedState;
  const [calendarView, setCalendarView] = useState<string>(() => {
    return localStorage.getItem('teamCalendarView') || 'agenda';
  });
  const [showViewPopover, setShowViewPopover] = useState(false);
  const calendarViewTriggerRef = useRef<HTMLIonButtonElement>(null);
  const calendarViewPortalRef = useRef<HTMLDivElement>(null);

  const getWeeksInMonth = useCallback((date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const firstWeekday = firstDay.getDay();
    const totalDays = lastDay.getDate() + firstWeekday;
    return Math.ceil(totalDays / 7);
  }, []);

  const calculateMonthHeight = useCallback(() => {
    const weeksInMonth = getWeeksInMonth(currentMonth);
    return BASE_HEIGHT + (MONTH_WEEK_HEIGHT * weeksInMonth);
  }, [currentMonth, getWeeksInMonth]);

  const calculateWeekViewHeight = useCallback(() => {
    return BASE_HEIGHT + WEEK_VIEW_HEIGHT;
  }, []);

  const updateHeight = useCallback((expanded: boolean) => {
    const newHeight = expanded ? calculateMonthHeight() : calculateWeekViewHeight();

    animate(height, newHeight, {
      type: "tween",
      duration: 0.3,
      ease: "easeOut"
    });

    setIsExpanded(expanded);
  }, [calculateMonthHeight, calculateWeekViewHeight, height, setIsExpanded]);

  const generateMonthDays = useCallback((date: Date) => {
    const days = [];
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    const startPadding = firstDay.getDay();
    const prevMonthLastDay = new Date(date.getFullYear(), date.getMonth(), 0);

    for (let i = startPadding - 1; i >= 0; i--) {
      days.push(addDays(prevMonthLastDay, -i));
    }

    for (let i = 0; i < lastDay.getDate(); i++) {
      days.push(addDays(firstDay, i));
    }

    const endPadding = 7 - ((days.length) % 7);
    if (endPadding < 7) {
      for (let i = 0; i < endPadding; i++) {
        days.push(addDays(lastDay, i + 1));
      }
    }

    return days;
  }, []);

  useEffect(() => {
    if (touchState.current.isDragging) {
      return;
    }

    if (isExpanded) {
      const newHeight = calculateMonthHeight();
      height.set(newHeight);
    }
  }, [currentMonth, isExpanded, calculateMonthHeight, height]);

  // Pure derivation, available from the first render (the old on-change
  // state version left the grids empty until the month changed).
  const displayedDays = React.useMemo(() => generateMonthDays(currentMonth), [currentMonth, generateMonthDays]);
  const prevMonth = React.useMemo(
    () => generateMonthDays(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)),
    [currentMonth, generateMonthDays]
  );
  const nextMonth = React.useMemo(
    () => generateMonthDays(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)),
    [currentMonth, generateMonthDays]
  );

  const handleDateSelection = useCallback((date: Date, forceCollapse: boolean = false): void => {
    if (touchState.current.swipe.isActive && !touchState.current.swipe.isVertical) {
      const newDate = startOfDay(date);
      setSelectedDate(newDate);

      const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
      if (currentMonth.getMonth() !== newDate.getMonth() ||
        currentMonth.getFullYear() !== newDate.getFullYear()) {
        setCurrentMonth(newMonth);
      }
      return;
    }

    const newDate = startOfDay(date);
    const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);

    if (isSameDay(selectedDate, newDate) &&
        currentMonth.getMonth() === newDate.getMonth() &&
        currentMonth.getFullYear() === newDate.getFullYear()) {
      return;
    }

    isAutoScrollingRef.current = true;

    setSelectedDate(newDate);
    setCurrentMonth(newMonth);

    if (forceCollapse) {
      updateHeight(false);
    }

    setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, 500);
  }, [updateHeight, currentMonth, selectedDate]);

  const handleDayClick = useCallback((date: Date) => {
    if (touchState.current.swipe.isActive && !touchState.current.swipe.isVertical) return;
    handleDateSelection(date, true);
  }, [handleDateSelection]);

  const handleHorizontalScroll = (direction: 'left' | 'right') => {
    isAutoScrollingRef.current = true;
    touchState.current.isNavigating = true;

    const distance = (containerRef.current?.offsetWidth || 0) + MONTH_GAP;
    const moveX = direction === 'left' ? distance : -distance;

    if (isExpanded) {
      const targetYear = currentMonth.getFullYear();
      const targetMonth = currentMonth.getMonth() + (direction === 'left' ? -1 : 1);

      const newMonthDate = new Date(targetYear, targetMonth, 1);
      const firstDayOfMonth = new Date(newMonthDate.getFullYear(), newMonthDate.getMonth(), 1);

      const newMonthWeeks = getWeeksInMonth(newMonthDate);
      const newHeight = BASE_HEIGHT + (MONTH_WEEK_HEIGHT * newMonthWeeks);

      setCurrentMonth(newMonthDate);
      setSelectedDate(firstDayOfMonth);
      height.set(newHeight);

    } else {
      const currentWeekStart = startOfWeek(selectedDate);
      const targetWeekStart = addWeeks(currentWeekStart, direction === 'left' ? -1 : 1);

      setSelectedDate(targetWeekStart);

      const newMonth = new Date(targetWeekStart.getFullYear(), targetWeekStart.getMonth(), 1);
      if (currentMonth.getMonth() !== targetWeekStart.getMonth() ||
        currentMonth.getFullYear() !== targetWeekStart.getFullYear()) {
        setCurrentMonth(newMonth);
      }

      height.set(calculateWeekViewHeight());
    }

    animate(x, moveX, {
      type: "spring",
      stiffness: 300,
      damping: 40,
      duration: 0.3
    }).then(() => {
      x.set(0);

      setTimeout(() => {
        touchState.current.isNavigating = false;
        isAutoScrollingRef.current = false;
      }, 200);
    });
  };

  const isNavButtonTouch = (e: React.TouchEvent): boolean => {
    const target = e.target as HTMLElement;
    return !!(
      target.closest('ion-button') ||
      target.closest('.ion-button') ||
      target.closest('ion-buttons') ||
      target.closest('[slot="start"]') ||
      target.closest('[slot="end"]') ||
      target.closest('ion-icon') ||
      target.closest('.ion-icon') ||
      target.closest('ion-toolbar') ||
      target.closest('.calendarNavHeader')
    );
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    const agendaElement = e.currentTarget.closest('.agendaContent');
    if (agendaElement) {
      touchState.current.isAgendaScrolling = true;
      return;
    }

    touchState.current.swipe.startX = e.touches[0].clientX;
    touchState.current.swipe.startY = e.touches[0].clientY;
    touchState.current.swipe.initialHeight = height.get();
    touchState.current.isDragging = true;
    touchState.current.swipe.isActive = false;
    touchState.current.swipe.isVertical = false;
    touchState.current.isAgendaScrolling = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    if (touchState.current.isAgendaScrolling || !touchState.current.isDragging) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchState.current.swipe.startX;
    const diffY = currentY - touchState.current.swipe.startY;

    if (!touchState.current.swipe.isActive && (Math.abs(diffX) > 5 || Math.abs(diffY) > 5)) {
      touchState.current.swipe.isActive = true;
      touchState.current.swipe.isVertical = Math.abs(diffY) > Math.abs(diffX);

      if (touchState.current.swipe.isVertical && diffY > 0 && !isExpanded) {
        setIsExpanded(true);

        setTimeout(() => {
          const monthHeight = calculateMonthHeight();
          height.set(monthHeight);
        }, 0);
      }
    }

    if (touchState.current.swipe.isActive) {
      if (touchState.current.swipe.isVertical) {
        const maxHeight = calculateMonthHeight();
        const minHeight = calculateWeekViewHeight();
        const newHeight = Math.max(minHeight, Math.min(maxHeight, touchState.current.swipe.initialHeight + diffY));

        height.set(newHeight);
      } else {
        x.set(diffX);
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    if (!touchState.current.isDragging) return;

    const currentX = e.changedTouches[0].clientX;
    const currentY = e.changedTouches[0].clientY;
    const diffX = currentX - touchState.current.swipe.startX;
    const diffY = currentY - touchState.current.swipe.startY;

    if (Math.abs(diffY) > Math.abs(diffX)) {
      const currentHeight = height.get();
      const monthHeight = calculateMonthHeight();
      const weekHeight = calculateWeekViewHeight();
      const threshold = weekHeight + ((monthHeight - weekHeight) / 2);

      const shouldBeExpanded = currentHeight > threshold;

      setIsExpanded(shouldBeExpanded);

      animate(height, shouldBeExpanded ? monthHeight : weekHeight, {
        type: "spring",
        stiffness: 200,
        damping: 20
      });
    } else {
      const threshold = 50;
      if (Math.abs(diffX) > threshold) {
        handleHorizontalScroll(diffX > 0 ? 'left' : 'right');
      } else {
        x.set(0);
      }
    }

    touchState.current.isDragging = false;
    touchState.current.swipe.isActive = false;
  };

  const handleVerticalDragStart = (e: React.TouchEvent) => {
    if (isNavButtonTouch(e)) {
      return;
    }

    touchState.current.swipe.startY = e.touches[0].clientY;
    touchState.current.swipe.initialHeight = height.get();
    touchState.current.isDragging = true;
  };

  const handleVerticalDragMove = (e: React.TouchEvent) => {
    if (!touchState.current.isDragging || touchState.current.isNavigating) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchState.current.swipe.startY;

    const userIsDraggingDown = diff > 0 && !isExpanded;

    const maxHeight = calculateMonthHeight();
    const minHeight = calculateWeekViewHeight();
    const newHeight = Math.max(minHeight, Math.min(maxHeight, touchState.current.swipe.initialHeight + diff));

    if (userIsDraggingDown) {
      animate(height, newHeight, {
        type: "spring",
        stiffness: 200,
        damping: 20
      });

      if (diff > 5) {
        setTimeout(() => setIsExpanded(true), 0);
      }
    } else {
      animate(height, newHeight, {
        type: "spring",
        stiffness: 200,
        damping: 20
      });
    }
  };

  const handleVerticalDragEnd = () => {
    if (!touchState.current.isDragging || touchState.current.isNavigating) return;

    const currentHeight = height.get();
    const monthHeight = calculateMonthHeight();
    const weekHeight = calculateWeekViewHeight();
    const threshold = weekHeight + ((monthHeight - weekHeight) / 2);
    const shouldBeExpanded = currentHeight > threshold;

    setIsExpanded(shouldBeExpanded);

    animate(height, shouldBeExpanded ? monthHeight : weekHeight, {
      type: "spring",
      stiffness: 200,
      damping: 20
    });

    touchState.current.isDragging = false;
  };

  const handleViewChange = (newView: string) => {
    setCalendarView(newView);
    localStorage.setItem('teamCalendarView', newView);
  };

  // Header icon sizes in px. Set as props (not CSS) so the stylesheet can't affect
  // them. The today icon is drawn in Tabler's calendar geometry (TodayCalendarIcon),
  // so it and TbCalendar / TbCalendarMonth are the same shape — one size for all
  // three keeps them equal. Ionicons fill more of their box, so the Agenda IonIcon
  // gets a slightly smaller one to look the same. Same values as Calendar.tsx.
  const HEADER_ICON_SIZE = 30;
  const HEADER_ION_ICON_SIZE = 28;
  // Inline style (not just the size prop) so the sizes can't be overridden.
  const tbIconStyle = { width: HEADER_ICON_SIZE, height: HEADER_ICON_SIZE } as const;

  const getViewIcon = () => {
    switch (calendarView) {
      case '3day':
        return <TbCalendarMonth size={HEADER_ICON_SIZE} className={styles.viewTriggerIcon} style={tbIconStyle} />;
      case 'day':
        return <TbCalendar size={HEADER_ICON_SIZE} className={styles.viewTriggerIcon} style={tbIconStyle} />;
      case 'agenda':
      default:
        return <IonIcon icon={agendaIcon} style={{ fontSize: HEADER_ION_ICON_SIZE }} />;
    }
  };

  function renderMonth(days: Date[], monthDate: Date) {
    return (
      <IonGrid className={styles.monthView} fixed={true} key={`month-${selectedDate.getTime()}-${monthDate.getTime()}`}>
        <IonRow className={styles.daysGrid}>
          {days.map((date) => {
            const isSelectedDay = isSameDay(selectedDate, date);
            const isToday = isSameDay(new Date(), date);
            const isInactiveMonth = date.getMonth() !== monthDate.getMonth();

            return (
              <IonCol
                key={date.getTime()}
                className={`${styles.dayCell} ${isSelectedDay ? styles.selected : ''}
                  ${isToday ? styles.today : ''}
                  ${isInactiveMonth ? styles.inactiveDay : ''}`}
                onClick={() => {
                  handleDayClick(date);
                }}
              >
                {date.getDate()}
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
    );
  }

  function renderWeekView(date: Date) {
    const weekStart = startOfWeek(date);
    return (
      <IonGrid className={styles.monthView} fixed={true} key={`week-${selectedDate.getTime()}-${date.getTime()}`}>
        <IonRow className={styles.daysGrid}>
          {Array.from({ length: 7 }).map((_, i) => {
            const dayDate = addDays(weekStart, i);
            const isSelectedDay = isSameDay(selectedDate, dayDate);
            const isToday = isSameDay(new Date(), dayDate);
            const isInactiveMonth = dayDate.getMonth() !== currentMonth.getMonth();

            return (
              <IonCol
                key={dayDate.getTime()}
                className={`${styles.dayCell} ${isSelectedDay ? styles.selected : ''} \
                  ${isToday ? styles.today : ''} \
                  ${isInactiveMonth ? styles.inactiveDay : ''}`}
                onClick={() => {
                  handleDayClick(dayDate);
                }}
              >
                {dayDate.getDate()}
              </IonCol>
            );
          })}
        </IonRow>
      </IonGrid>
    );
  }

  return (
    <IonPage data-pageid="team-calendar">
      {/* Portal target for the calendar-view PortalDropdown below — a direct
          IonPage child (fixed, inset:0) rather than nested inside IonButtons,
          which doesn't reliably render/size a plain non-IonButton child
          slotted alongside real IonButtons. */}
      <div ref={calendarViewPortalRef} className={styles.pickerPortalRoot} />
      <IonHeader className='ion-no-border'>
        <IonToolbar className={styles.toolbarHeader}>
          <IonButtons slot="start">
            <IonBackButton defaultHref='/team/messages' icon={chevronBack} type="button" text="" />
            <IonCol className={styles.titleStack}>
              <IonTitle size="small">{t('pages.teamMessaging.TeamCalendar.teamCalendar')}</IonTitle>
              <IonText className={styles.subtitle}>{t('pages.teamMessaging.TeamCalendar.scheduleEvents')}</IonText>
            </IonCol>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              className={styles.circleButton}
              aria-label={t('pages.teamMessaging.TeamCalendar.goToToday')}
              onClick={() => {
                const today = new Date();
                const newDate = startOfDay(today);
                const newMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);

                isAutoScrollingRef.current = true;
                setSelectedDate(newDate);
                setCurrentMonth(newMonth);
                updateHeight(false);

                setTimeout(() => {
                  isAutoScrollingRef.current = false;
                }, 500);
              }}
            >
              <TodayCalendarIcon size={HEADER_ICON_SIZE} className={styles.todayIcon} />
            </IonButton>
            <IonButton
              ref={calendarViewTriggerRef}
              className={`${styles.circleButton} ${showViewPopover ? styles.active : ''}`}
              aria-label={t('pages.teamMessaging.TeamCalendar.changeCalendarView')}
              onClick={() => setShowViewPopover(v => !v)}
            >
              {getViewIcon()}
            </IonButton>
            <PortalDropdown
              isOpen={showViewPopover}
              anchorRef={calendarViewTriggerRef}
              portalRef={calendarViewPortalRef}
              onClose={() => setShowViewPopover(false)}
              align="end"
              title={t('pages.teamMessaging.TeamCalendar.calendarView')}
              compact
              options={[
                {
                  value: 'agenda',
                  label: t('pages.teamMessaging.TeamCalendar.agenda'),
                  icon: agendaIcon,
                  color: calendarView === 'agenda' ? 'primary' : 'medium',
                  selected: calendarView === 'agenda',
                  handler: () => handleViewChange('agenda'),
                },
                {
                  value: '3day',
                  label: t('pages.teamMessaging.TeamCalendar.3Day'),
                  icon: <TbCalendarMonth size="1.35em" />,
                  color: calendarView === '3day' ? 'primary' : 'medium',
                  selected: calendarView === '3day',
                  handler: () => handleViewChange('3day'),
                },
                {
                  value: 'day',
                  label: t('pages.teamMessaging.TeamCalendar.day'),
                  icon: <TbCalendar size="1.35em" />,
                  color: calendarView === 'day' ? 'primary' : 'medium',
                  selected: calendarView === 'day',
                  handler: () => handleViewChange('day'),
                },
              ]}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false}>
        <IonGrid className={styles.teamCalendarContainer} fixed={true}>
          <IonCard className={styles.calendar}>
            <IonCardHeader className={styles.calendarNavHeader}>
              <IonToolbar className="calendar-nav-toolbar">
                <IonButtons slot="start">
                  <IonButton
                    fill="clear"
                    shape='round'
                    aria-label={t('pages.teamMessaging.TeamCalendar.previousPeriod')}
                    onClick={() => {
                      if (isExpanded) {
                        handleHorizontalScroll('left');
                      } else {
                        const newDate = addWeeks(selectedDate, -1);
                        handleDateSelection(newDate);
                      }
                    }}
                  >
                    <IonIcon icon={caretBack} />
                  </IonButton>
                </IonButtons>

                <IonTitle className={styles.monthTitle}>
                  {currentMonth.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' })}
                </IonTitle>

                <IonButtons slot="end">
                  <IonButton
                    fill="clear"
                    shape='round'
                    aria-label={t('pages.teamMessaging.TeamCalendar.nextPeriod')}
                    onClick={() => {
                      if (isExpanded) {
                        handleHorizontalScroll('right');
                      } else {
                        const newDate = addWeeks(selectedDate, 1);
                        handleDateSelection(newDate);
                      }
                    }}
                  >
                    <IonIcon icon={caretForward} />
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonCardHeader>

            <IonGrid className={styles.weekDays}>
              <IonRow>
                {getWeekdayNames('short').map(day => (
                  <IonCol key={day} className={styles.weekDay}>{day}</IonCol>
                ))}
              </IonRow>
            </IonGrid>
            <IonCard
              ref={containerRef}
              className={styles.calendarWrapper}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <motion.div
                key={`calendar-${selectedDate.getTime()}`}
                className={`${styles.monthSlider} ${isExpanded ? styles.expanded : ''}`}
                style={{
                  transform: xTransform,
                  height,
                  overflow: 'hidden'
                }}
              >
                {!isExpanded ? (
                  <>
                    {renderWeekView(addWeeks(selectedDate, -1))}
                    {renderWeekView(selectedDate)}
                    {renderWeekView(addWeeks(selectedDate, 1))}
                  </>
                ) : (
                  <>
                    <IonCol className={styles.monthViewWidth}>
                      {renderMonth(prevMonth, new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    </IonCol>
                    <IonCol className={styles.monthViewWidth}>
                      {renderMonth(displayedDays, currentMonth)}
                    </IonCol>
                    <IonCol className={styles.monthViewWidth}>
                      {renderMonth(nextMonth, new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    </IonCol>
                  </>
                )}
              </motion.div>
            </IonCard>

            <IonItem lines="none" className={styles.dragArea}
              onTouchStart={handleVerticalDragStart}
              onTouchMove={handleVerticalDragMove}
              onTouchEnd={handleVerticalDragEnd}
            >
              <IonLabel className={styles.dragHandleContainer}>
                <IonProgressBar className={styles.dragHandle} value={0} />
              </IonLabel>
            </IonItem>
          </IonCard>

          <TeamAgenda
            selectedDate={selectedDate}
            isCalendarExpanded={isExpanded}
            events={teamEvents}
            isLoading={isLoadingEvents}
            onDateChange={(date: Date) => {
              if (!isAutoScrollingRef.current && !touchState.current.isNavigating) {
                if (!isSameDay(selectedDate, date)) {
                  setSelectedDate(new Date(date));

                  const newMonth = new Date(date.getFullYear(), date.getMonth(), 1);
                  if (currentMonth.getMonth() !== date.getMonth() ||
                    currentMonth.getFullYear() !== date.getFullYear()) {
                    setCurrentMonth(newMonth);
                  }
                }
              }
            }}
          />
        </IonGrid>
      </IonContent>

      <IonFab className={styles.calendarFab}>
        <IonFabButton mode="ios" aria-label={t('pages.teamMessaging.TeamCalendar.addNewBooking')} >
          <IonIcon icon={add} />
        </IonFabButton>
      </IonFab>
    </IonPage>
  );
};

export default TeamCalendar;
